import { useEffect, useState } from "react";
import {
  UserPlus,
  Loader2,
  AlertCircle,
  MoreHorizontal,
  Copy,
  CheckCircle2,
} from "lucide-react";
import AppShell from "../components/Layout/AppShell";
import api from "../lib/api";
import { useAuth } from "../lib/auth-context";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const STROKE = 1.5;

const ROLE_LABEL = {
  admin: "Admin",
  recruiter: "Recruiter",
  hiring_manager: "Hiring Manager",
  viewer: "Viewer",
};
const ALL_ROLES = Object.keys(ROLE_LABEL);

function RoleBadge({ role }) {
  const styles = {
    admin: "bg-emerald-50 text-emerald-800 border border-emerald-800/15",
    recruiter: "bg-ink-100 text-ink-700 border border-ink-200",
    hiring_manager: "bg-amber-50 text-amber-800 border border-amber-200",
    viewer: "bg-ink-50 text-ink-600 border border-ink-200",
  }[role] || "bg-ink-100 text-ink-700 border border-ink-200";
  return (
    <span
      className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ${styles}`}
      data-testid={`role-badge-${role}`}
    >
      {ROLE_LABEL[role] || role}
    </span>
  );
}

function InviteModal({ open, onOpenChange, onCreated }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("recruiter");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [inviteLink, setInviteLink] = useState(null);

  const reset = () => {
    setEmail("");
    setRole("recruiter");
    setError("");
    setInviteLink(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/invite", { email, role });
      // The invite token isn't returned in list endpoints, but is in the create response
      const link = `${window.location.origin}/invite/${data.token || ""}`;
      setInviteLink(link);
      onCreated();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to send invite");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="invite-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-medium">
            {inviteLink ? "Invite created" : "Invite a teammate"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-ink-500">
            {inviteLink
              ? "Share the link below. The invite expires in 14 days."
              : "They'll receive access to your workspace with the role you choose."}
          </DialogDescription>
        </DialogHeader>

        {inviteLink ? (
          <div className="space-y-4 py-2">
            <div className="rounded-md border border-ink-200 bg-ink-50 px-3 py-2 text-[12px] font-mono text-ink-700 break-all" data-testid="invite-link">
              {inviteLink}
            </div>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(inviteLink);
                toast.success("Invite link copied");
              }}
              className="hp-btn-outline w-full"
              data-testid="invite-copy-btn"
            >
              <Copy className="h-4 w-4" strokeWidth={STROKE} /> Copy link
            </button>
            <button
              type="button"
              onClick={() => { reset(); onOpenChange(false); }}
              className="hp-btn-ghost w-full"
              data-testid="invite-done-btn"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-2" data-testid="invite-form">
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Email
              </label>
              <input
                type="email"
                required
                className="hp-input mt-1.5"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="invite-email"
              />
            </div>
            <div>
              <label className="text-[12px] font-medium uppercase tracking-wider text-ink-500">
                Role
              </label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-1.5 hp-input" data-testid="invite-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin" data-testid="role-option-admin">Admin · Full access, billing, team</SelectItem>
                  <SelectItem value="recruiter" data-testid="role-option-recruiter">Recruiter · Record & view interviews</SelectItem>
                  <SelectItem value="hiring_manager" data-testid="role-option-hiring_manager">Hiring Manager · Scorecards & comments</SelectItem>
                  <SelectItem value="viewer" data-testid="role-option-viewer">Viewer · Read-only (HR / compliance)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {error && (
              <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-[13px] text-red-700" data-testid="invite-error">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={STROKE} /> {error}
              </div>
            )}
            <DialogFooter>
              <button
                type="button"
                onClick={() => { reset(); onOpenChange(false); }}
                className="hp-btn-ghost"
                data-testid="invite-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="hp-btn-primary"
                data-testid="invite-submit"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" strokeWidth={STROKE} />}
                Create invite
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Team() {
  const { member: currentMember } = useAuth();
  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const isAdmin = currentMember?.role === "admin";

  const load = async () => {
    setLoading(true);
    try {
      const { data: m } = await api.get("/team/members");
      setMembers(m);
      if (isAdmin) {
        const { data: inv } = await api.get("/team/invites");
        setInvites(inv);
      } else {
        setInvites([]);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load team");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const handleRevokeInvite = async (inviteId) => {
    try {
      await api.delete(`/team/invites/${inviteId}`);
      toast.success("Invite revoked");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to revoke invite");
    }
  };

  const handleRemoveMember = async (memberId) => {
    if (!confirm("Remove this member from the workspace?")) return;
    try {
      await api.delete(`/team/members/${memberId}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove member");
    }
  };

  const handleChangeRole = async (memberId, newRole) => {
    try {
      await api.patch(`/team/members/${memberId}/role`, { role: newRole });
      toast.success("Role updated");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update role");
    }
  };

  return (
    <AppShell>
      <div data-testid="team-page" className="space-y-10">
        <header className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="hp-section-label">Team</div>
            <h1 className="font-display mt-2 text-3xl font-medium tracking-tight text-ink-900">
              Team members
            </h1>
            <p className="mt-1 text-[14px] text-ink-500">
              Admins can invite new members, change roles, and remove access.
            </p>
          </div>
          {isAdmin && (
            <button
              onClick={() => setInviteOpen(true)}
              className="hp-btn-primary"
              data-testid="invite-member-btn"
            >
              <UserPlus className="h-4 w-4" strokeWidth={STROKE} />
              Invite member
            </button>
          )}
        </header>

        <section>
          <div className="hp-section-label mb-3">Active members</div>
          <div className="hp-card overflow-hidden">
            <table className="w-full" data-testid="members-table">
              <thead className="bg-ink-50 border-b border-ink-200">
                <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3">Joined</th>
                  {isAdmin && <th className="px-5 py-3 w-10" />}
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {loading && (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="px-5 py-12 text-center text-ink-400">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin" strokeWidth={STROKE} />
                    </td>
                  </tr>
                )}
                {!loading && members.map((m) => (
                  <tr key={m.member_id} className="hover:bg-ink-50 transition" data-testid={`member-row-${m.member_id}`}>
                    <td className="px-5 py-3 text-[14px] font-medium text-ink-900">{m.name || "—"}</td>
                    <td className="px-5 py-3 text-[14px] text-ink-700">{m.email || "—"}</td>
                    <td className="px-5 py-3"><RoleBadge role={m.role} /></td>
                    <td className="px-5 py-3 text-[13px] text-ink-500">
                      {new Date(m.joined_at).toLocaleDateString()}
                    </td>
                    {isAdmin && (
                      <td className="px-5 py-3 text-right">
                        {m.user_id !== currentMember.user_id && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                data-testid={`member-actions-${m.member_id}`}
                              >
                                <MoreHorizontal className="h-4 w-4" strokeWidth={STROKE} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {ALL_ROLES.filter(r => r !== m.role).map(r => (
                                <DropdownMenuItem
                                  key={r}
                                  onClick={() => handleChangeRole(m.member_id, r)}
                                  data-testid={`change-role-${m.member_id}-${r}`}
                                >
                                  Make {ROLE_LABEL[r]}
                                </DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleRemoveMember(m.member_id)}
                                className="text-red-700 focus:text-red-700"
                                data-testid={`remove-member-${m.member_id}`}
                              >
                                Remove from workspace
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {isAdmin && (
          <section>
            <div className="hp-section-label mb-3">Pending invites</div>
            <div className="hp-card overflow-hidden">
              <table className="w-full" data-testid="invites-table">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wider text-ink-500">
                    <th className="px-5 py-3">Email</th>
                    <th className="px-5 py-3">Role</th>
                    <th className="px-5 py-3">Invited by</th>
                    <th className="px-5 py-3">Expires</th>
                    <th className="px-5 py-3 w-10" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {!loading && invites.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-10 text-center text-[13px] text-ink-400" data-testid="invites-empty">
                        No pending invites.
                      </td>
                    </tr>
                  )}
                  {invites.map((inv) => (
                    <tr key={inv.invite_id} className="hover:bg-ink-50 transition" data-testid={`invite-row-${inv.invite_id}`}>
                      <td className="px-5 py-3 text-[14px] font-medium text-ink-900">{inv.email}</td>
                      <td className="px-5 py-3"><RoleBadge role={inv.role} /></td>
                      <td className="px-5 py-3 text-[13px] text-ink-500">{inv.invited_by_name || "—"}</td>
                      <td className="px-5 py-3 text-[13px] text-ink-500">
                        {new Date(inv.expires_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button
                          onClick={() => handleRevokeInvite(inv.invite_id)}
                          className="text-[13px] font-medium text-red-700 hover:underline"
                          data-testid={`revoke-invite-${inv.invite_id}`}
                        >
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
      <InviteModal open={inviteOpen} onOpenChange={setInviteOpen} onCreated={load} />
    </AppShell>
  );
}
